import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { AuthContext } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatMessageTime = (timestamp) => {
  const now = new Date();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (minutes < 1) return 'just now';
  if (hours < 1) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return timestamp.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatConversationTime = (timestamp) => {
  const now = new Date();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  return timestamp.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** Initials avatar for a username */
const Avatar = ({ username, size = 'md' }) => {
  const initials = username
    ? username.slice(0, 2).toUpperCase()
    : '??';
  const sizeClasses = size === 'sm'
    ? 'w-7 h-7 text-[10px]'
    : 'w-8 h-8 text-xs';
  return (
    <div
      className={clsx(
        sizeClasses,
        'rounded-full bg-gradient-to-br from-primary-300 to-primary-500 flex items-center justify-center font-semibold text-white flex-shrink-0 shadow-soft-sm'
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const mockConversations = [
  {
    id: 1,
    type: 'event_group',
    name: 'Beach Volleyball - Saturday',
    participants: [
      { id: 1, username: 'john_doe', avatar: null },
      { id: 2, username: 'jane_smith', avatar: null },
      { id: 3, username: 'mike_wilson', avatar: null },
    ],
    lastMessage: 'See you all at the beach!',
    lastMessageTime: new Date(Date.now() - 30 * 60 * 1000),
    unreadCount: 2,
    eventId: 123,
  },
  {
    id: 2,
    type: 'event_group',
    name: 'Coffee Chat Meetup',
    participants: [
      { id: 4, username: 'alice_brown', avatar: null },
      { id: 5, username: 'bob_jones', avatar: null },
    ],
    lastMessage: 'Changed location to Downtown Cafe',
    lastMessageTime: new Date(Date.now() - 2 * 60 * 60 * 1000),
    unreadCount: 0,
    eventId: 124,
  },
];

const mockMessages = {
  1: [
    {
      id: 1,
      senderId: 2,
      senderUsername: 'jane_smith',
      content: "Hey everyone! Excited for tomorrow's game!",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      type: 'text',
    },
    {
      id: 2,
      senderId: 3,
      senderUsername: 'mike_wilson',
      content: 'Same here! What time should we meet?',
      timestamp: new Date(Date.now() - 90 * 60 * 1000),
      type: 'text',
    },
    {
      id: 3,
      senderId: 1,
      senderUsername: 'john_doe',
      content: "Let's meet at 10 AM at the main entrance",
      timestamp: new Date(Date.now() - 60 * 60 * 1000),
      type: 'text',
    },
    {
      id: 4,
      senderId: 2,
      senderUsername: 'jane_smith',
      content: {
        eventId: 125,
        title: 'Sunday Brunch',
        datetime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      },
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      type: 'event_share',
    },
    {
      id: 5,
      senderId: 3,
      senderUsername: 'mike_wilson',
      content: 'See you all at the beach!',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      type: 'text',
    },
  ],
};

const mockEvents = [
  { id: 125, title: 'Sunday Brunch', datetime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) },
  { id: 126, title: 'Movie Night', datetime: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) },
  { id: 127, title: 'Hiking Adventure', datetime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * A single message bubble. Renders a timestamp on hover.
 * Groups consecutive messages from the same sender (Discord-style).
 */
const MessageBubble = ({ message, isOwn, isFirst }) => {
  const [hovered, setHovered] = useState(false);

  if (message.type === 'event_share') {
    return (
      <div
        className={clsx('flex items-end gap-2 group', isOwn ? 'flex-row-reverse' : 'flex-row')}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Avatar spacer / avatar */}
        {!isOwn && (
          <div className="w-8 flex-shrink-0 self-end">
            {isFirst && <Avatar username={message.senderUsername} />}
          </div>
        )}

        <div className="flex flex-col gap-0.5 max-w-xs sm:max-w-sm lg:max-w-md">
          {!isOwn && isFirst && (
            <span className="text-[11px] font-semibold text-surface-500 ml-1 mb-0.5">
              {message.senderUsername}
            </span>
          )}

          <div
            className={clsx(
              'px-3 py-2.5 rounded-2xl shadow-soft-sm',
              isOwn
                ? 'bg-primary-500 text-white rounded-br-md'
                : 'bg-white border border-surface-200 text-surface-800 rounded-bl-md'
            )}
          >
            <p
              className={clsx(
                'text-[11px] font-medium mb-2',
                isOwn ? 'text-primary-100' : 'text-surface-400'
              )}
            >
              Shared an event
            </p>
            <div
              className={clsx(
                'rounded-xl p-3 border',
                isOwn
                  ? 'bg-primary-600/50 border-primary-400/50'
                  : 'bg-surface-50 border-surface-200'
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <svg
                  className={clsx('w-3.5 h-3.5 flex-shrink-0', isOwn ? 'text-primary-200' : 'text-primary-500')}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h4
                  className={clsx(
                    'text-xs font-semibold leading-tight',
                    isOwn ? 'text-white' : 'text-surface-900'
                  )}
                >
                  {message.content.title}
                </h4>
              </div>
              <p className={clsx('text-[11px]', isOwn ? 'text-primary-200' : 'text-surface-500')}>
                {new Date(message.content.datetime).toLocaleString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>

          {/* Hover timestamp */}
          <AnimatePresence>
            {hovered && (
              <motion.span
                key="ts"
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -2 }}
                transition={{ duration: 0.12 }}
                className={clsx(
                  'text-[10px] text-surface-400 px-1',
                  isOwn ? 'text-right self-end' : 'text-left self-start'
                )}
              >
                {formatMessageTime(message.timestamp)}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx('flex items-end gap-2 group', isOwn ? 'flex-row-reverse' : 'flex-row')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Avatar column — always reserves space so bubbles align */}
      {!isOwn && (
        <div className="w-8 flex-shrink-0 self-end">
          {isFirst && <Avatar username={message.senderUsername} />}
        </div>
      )}

      <div className="flex flex-col gap-0.5 max-w-xs sm:max-w-sm lg:max-w-md">
        {/* Sender name (only on first bubble in a group) */}
        {!isOwn && isFirst && (
          <span className="text-[11px] font-semibold text-surface-500 ml-1 mb-0.5">
            {message.senderUsername}
          </span>
        )}

        <div
          className={clsx(
            'px-3.5 py-2 text-sm leading-relaxed shadow-soft-sm',
            isOwn
              ? 'bg-primary-500 text-white rounded-2xl rounded-br-md'
              : 'bg-white border border-surface-200 text-surface-800 rounded-2xl rounded-bl-md'
          )}
        >
          {message.content}
        </div>

        {/* Hover timestamp */}
        <AnimatePresence>
          {hovered && (
            <motion.span
              key="ts"
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -2 }}
              transition={{ duration: 0.12 }}
              className={clsx(
                'text-[10px] text-surface-400 px-1',
                isOwn ? 'text-right self-end' : 'text-left self-start'
              )}
            >
              {formatMessageTime(message.timestamp)}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/**
 * Conversation list item.
 */
const ConversationItem = ({ conversation, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={clsx(
      'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors duration-100 border-b border-surface-100 last:border-0',
      isActive
        ? 'bg-primary-50 border-l-2 border-l-primary-400'
        : 'hover:bg-surface-50 border-l-2 border-l-transparent'
    )}
    aria-current={isActive ? 'true' : undefined}
  >
    {/* Group icon */}
    <div
      className={clsx(
        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold shadow-soft-sm',
        isActive
          ? 'bg-primary-500 text-white'
          : 'bg-surface-100 text-surface-600'
      )}
      aria-hidden="true"
    >
      {conversation.name.slice(0, 2).toUpperCase()}
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <span
          className={clsx(
            'text-sm font-semibold truncate',
            isActive ? 'text-primary-700' : 'text-surface-900'
          )}
        >
          {conversation.name}
        </span>
        <span className="text-[10px] text-surface-400 flex-shrink-0">
          {formatConversationTime(conversation.lastMessageTime)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-1">
        <p className="text-xs text-surface-500 truncate">{conversation.lastMessage}</p>
        {conversation.unreadCount > 0 && (
          <span className="flex-shrink-0 min-w-[18px] h-[18px] bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {conversation.unreadCount}
          </span>
        )}
      </div>
    </div>
  </button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MessengerInterface = () => {
  const { currentUser } = useContext(AuthContext);

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showEventShare, setShowEventShare] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  /** Controls mobile drawer visibility */
  const [drawerOpen, setDrawerOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // -------------------------------------------------------------------------
  // Data fetching (mock)
  // -------------------------------------------------------------------------

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      // Replace with: const response = await api.get('/api/messages/conversations');
      setConversations(mockConversations);
      if (mockConversations.length > 0) {
        setActiveConversation(mockConversations[0]);
      }
    } catch (_err) {
      // handle error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      // Replace with: const response = await api.get(`/api/messages/conversations/${conversationId}/messages`);
      setMessages(mockMessages[conversationId] || []);
    } catch (_err) {
      // handle error
    }
  }, []);

  const fetchAvailableEvents = useCallback(async () => {
    try {
      // Replace with: const response = await api.get('/api/events/my-events');
      setAvailableEvents(mockEvents);
    } catch (_err) {
      // handle error
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchAvailableEvents();
  }, [fetchConversations, fetchAvailableEvents]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
    }
  }, [activeConversation, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const localMessage = {
        id: Date.now(),
        senderId: currentUser?.id,
        senderUsername: currentUser?.username || 'you',
        content: newMessage.trim(),
        timestamp: new Date(),
        type: 'text',
      };
      setMessages((prev) => [...prev, localMessage]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (_err) {
      // handle error
    } finally {
      setSendingMessage(false);
    }
  };

  const shareEvent = async (event) => {
    if (!activeConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const localMessage = {
        id: Date.now(),
        senderId: currentUser?.id,
        senderUsername: currentUser?.username || 'you',
        content: { eventId: event.id, title: event.title, datetime: event.datetime },
        timestamp: new Date(),
        type: 'event_share',
      };
      setMessages((prev) => [...prev, localMessage]);
      setShowEventShare(false);
    } catch (_err) {
      // handle error
    } finally {
      setSendingMessage(false);
    }
  };

  const handleConversationSelect = (conversation) => {
    setActiveConversation(conversation);
    setDrawerOpen(false); // close mobile drawer on selection
  };

  // -------------------------------------------------------------------------
  // Message grouping (Discord-style)
  // -------------------------------------------------------------------------

  /**
   * Returns an array of { message, isFirst } pairs.
   * isFirst = true when this is the first bubble of a consecutive run by the same sender.
   */
  const groupedMessages = messages.map((message, index) => {
    const prev = messages[index - 1];
    const isFirst =
      !prev ||
      prev.senderId !== message.senderId ||
      message.timestamp - prev.timestamp > 5 * 60 * 1000; // new group after 5 min gap
    return { message, isFirst };
  });

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const ConversationSidebar = (
    <div className="flex flex-col h-full">
      {/* Sidebar header */}
      <div className="px-4 py-4 border-b border-surface-200 flex-shrink-0">
        <h2 className="text-h3 text-surface-900">Messages</h2>
        <p className="text-xs text-surface-400 mt-0.5">Event group chats</p>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-3">
            <svg
              className="w-10 h-10 text-surface-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="text-sm text-surface-400">No conversations yet</p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isActive={activeConversation?.id === conversation.id}
              onClick={() => handleConversationSelect(conversation)}
            />
          ))
        )}
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-surface-50">
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-surface-200 border-t-primary-500 animate-spin"
            role="status"
            aria-label="Loading messages"
          />
          <p className="text-sm text-surface-400">Loading messages…</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main layout
  // -------------------------------------------------------------------------

  return (
    <div className="flex h-full bg-surface-50 overflow-hidden">
      {/* ------------------------------------------------------------------ */}
      {/* Desktop sidebar                                                      */}
      {/* ------------------------------------------------------------------ */}
      <aside className="hidden md:flex md:flex-col w-72 lg:w-80 bg-white border-r border-surface-200 flex-shrink-0 h-full">
        {ConversationSidebar}
      </aside>

      {/* ------------------------------------------------------------------ */}
      {/* Mobile drawer overlay                                               */}
      {/* ------------------------------------------------------------------ */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-surface-900/40 md:hidden"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            {/* Drawer */}
            <motion.aside
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-soft-lg md:hidden flex flex-col"
            >
              {/* Close button */}
              <button
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 p-1.5 rounded-lg text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors"
                aria-label="Close conversation list"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {ConversationSidebar}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ------------------------------------------------------------------ */}
      {/* Chat area                                                            */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {activeConversation ? (
          <>
            {/* Chat header */}
            <header className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-white border-b border-surface-200 shadow-soft-sm">
              {/* Mobile: hamburger to open drawer */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="md:hidden p-1.5 rounded-lg text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors"
                aria-label="Open conversation list"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <div
                className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-300 to-primary-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-soft-sm"
                aria-hidden="true"
              >
                {activeConversation.name.slice(0, 2).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-surface-900 truncate">
                  {activeConversation.name}
                </h3>
                <p className="text-xs text-surface-400">
                  {activeConversation.participants.length} participants
                </p>
              </div>

              {/* Event share toggle — visible in header on mobile */}
              <button
                onClick={() => setShowEventShare((v) => !v)}
                className={clsx(
                  'p-2 rounded-xl transition-colors',
                  showEventShare
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-surface-400 hover:text-primary-500 hover:bg-primary-50'
                )}
                aria-label="Share an event"
                aria-expanded={showEventShare}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </header>

            {/* Event share panel */}
            <AnimatePresence>
              {showEventShare && (
                <motion.div
                  key="event-share"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="flex-shrink-0 overflow-hidden bg-white border-b border-surface-200"
                >
                  <div className="px-4 py-3">
                    <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
                      Share an event
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {availableEvents.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => shareEvent(event)}
                          disabled={sendingMessage}
                          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-50 border border-surface-200 hover:bg-primary-50 hover:border-primary-300 transition-colors text-left disabled:opacity-50"
                        >
                          <svg
                            className="w-3.5 h-3.5 text-primary-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <div>
                            <p className="text-xs font-semibold text-surface-800">{event.title}</p>
                            <p className="text-[10px] text-surface-400">
                              {new Date(event.datetime).toLocaleDateString(undefined, {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Messages scroll area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scroll-smooth">
              {groupedMessages.map(({ message, isFirst }, index) => {
                const isOwn = message.senderId === currentUser?.id;

                // Date separator: show when day changes between consecutive messages
                const prevMessage = index > 0 ? groupedMessages[index - 1].message : null;
                const showDateSeparator =
                  !prevMessage ||
                  new Date(message.timestamp).toDateString() !==
                    new Date(prevMessage.timestamp).toDateString();

                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <div className="flex items-center gap-3 py-2">
                        <div className="flex-1 h-px bg-surface-200" />
                        <span className="text-[10px] font-semibold text-surface-400 uppercase tracking-wide whitespace-nowrap">
                          {new Date(message.timestamp).toLocaleDateString(undefined, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                        <div className="flex-1 h-px bg-surface-200" />
                      </div>
                    )}
                    {/* Spacing between groups */}
                    {isFirst && index > 0 && !showDateSeparator && (
                      <div className="h-2" aria-hidden="true" />
                    )}
                    <MessageBubble
                      message={message}
                      isOwn={isOwn}
                      isFirst={isFirst}
                    />
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message input */}
            <div className="flex-shrink-0 bg-white border-t border-surface-200 px-4 py-3">
              <form onSubmit={sendMessage} className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      sendMessage(e);
                    }
                  }}
                  placeholder="Send a message…"
                  disabled={sendingMessage}
                  className="flex-1 px-4 py-2.5 rounded-2xl bg-surface-100 border border-surface-200 text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow disabled:opacity-60"
                  aria-label="Message input"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sendingMessage}
                  className="flex-shrink-0 w-10 h-10 rounded-2xl bg-primary-500 hover:bg-primary-600 disabled:bg-surface-200 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors shadow-soft-sm"
                  aria-label="Send message"
                >
                  {sendingMessage ? (
                    <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    <svg className="w-4 h-4 translate-x-px" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  )}
                </button>
              </form>
            </div>
          </>
        ) : (
          /* Empty state — no conversation selected */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            {/* Mobile: show button to open drawer */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden mb-2 px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors shadow-soft-sm"
            >
              View conversations
            </button>

            <div className="w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-surface-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-surface-700">No conversation selected</p>
              <p className="text-xs text-surface-400 mt-1">
                Choose a conversation from the list to start chatting.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessengerInterface;
